import { useState, useEffect, useRef } from 'react';
import { WifiOff, RefreshCw, CloudOff, CheckCircle, Clock } from 'lucide-react';
import { useNetworkStatus } from '../hooks/useNetworkStatus.js';
import { getSyncQueueCount } from '../hooks/useIndexedDB.js';

function formatRelativeTime(timestamp: number): string {
  const diff = Math.floor((Date.now() - timestamp) / 1000);
  if (diff < 10) return 'zojuist';
  if (diff < 60) return `${diff}s geleden`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m geleden`;
  return `${Math.floor(diff / 3600)}u geleden`;
}

export function OfflineBanner() {
  const { isOnline, wasOffline } = useNetworkStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [showReconnected, setShowReconnected] = useState(false);
  const [initialPending, setInitialPending] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [relativeTime, setRelativeTime] = useState('');
  const prevPendingRef = useRef(0);

  // Poll sync queue count
  useEffect(() => {
    const update = () => getSyncQueueCount().then(count => {
      setPendingCount(prev => {
        // Track when items are synced (count decreased while online)
        if (prev > 0 && count < prev && navigator.onLine) {
          setLastSyncAt(Date.now());
        }
        if (count === 0 && prev > 0 && navigator.onLine) {
          setLastSyncAt(Date.now());
        }
        return count;
      });
    }).catch(() => {});
    update();
    const interval = setInterval(update, 3000);
    return () => clearInterval(interval);
  }, []);

  // Track initial pending count when syncing starts
  useEffect(() => {
    if (isOnline && pendingCount > 0 && initialPending === 0) {
      setInitialPending(pendingCount);
    }
    if (pendingCount === 0) {
      setInitialPending(0);
    }
  }, [isOnline, pendingCount, initialPending]);

  // Update relative time display
  useEffect(() => {
    if (!lastSyncAt) return;
    const update = () => setRelativeTime(formatRelativeTime(lastSyncAt));
    update();
    const interval = setInterval(update, 10_000);
    return () => clearInterval(interval);
  }, [lastSyncAt]);

  // Show "reconnected" banner briefly
  useEffect(() => {
    if (isOnline && wasOffline) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  // Offline banner (persistent, not dismissable)
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-600 text-white text-center py-2 px-4 text-sm font-medium safe-top">
        <div className="flex items-center justify-center gap-2">
          <WifiOff size={16} />
          <span>Je bent offline — wijzigingen worden gesynchroniseerd</span>
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center justify-center gap-1.5 mt-1 text-xs text-amber-100">
            <CloudOff size={12} />
            <span>{pendingCount} wijziging{pendingCount !== 1 ? 'en' : ''} wacht{pendingCount === 1 ? '' : 'en'} op synchronisatie</span>
          </div>
        )}
        {lastSyncAt && (
          <div className="flex items-center justify-center gap-1 mt-0.5 text-[10px] text-amber-200/70">
            <Clock size={9} />
            <span>Laatst gesynchroniseerd: {relativeTime}</span>
          </div>
        )}
      </div>
    );
  }

  // Syncing banner with progress bar
  if (isOnline && pendingCount > 0) {
    const progress = initialPending > 0
      ? Math.round(((initialPending - pendingCount) / initialPending) * 100)
      : 0;

    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] safe-top">
        <div className="bg-blue-600 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2">
          <RefreshCw size={16} className="animate-spin" />
          <span>{pendingCount} wijziging{pendingCount !== 1 ? 'en' : ''} synchroniseren...</span>
        </div>
        {/* Sync progress bar */}
        <div className="h-1 bg-blue-800">
          <div
            className="h-full bg-blue-300 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }

  // Reconnected banner
  if (showReconnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-green-600 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2 animate-slide-down safe-top">
        <CheckCircle size={16} />
        {initialPending > 0
          ? `${initialPending} wijziging${initialPending !== 1 ? 'en' : ''} gesynchroniseerd`
          : 'Verbinding hersteld — alles gesynchroniseerd'}
      </div>
    );
  }

  return null;
}
